import { useCallback, useEffect, useState } from 'react';
import { Box, styled } from '@mui/material';
import { observer } from 'mobx-react-lite';
import { useSnackbar } from 'notistack';
import { IEmployee, IOrganizationChart } from 'src/@types/apiResponseTypes';
import { commonApi, employeeApi } from 'src/api';
import { Page } from 'src/components/Page';

import EmpDetailDialog, {
  SimpleDialogProps as EmpDetailDialogProps,
} from '../organization-layout-old/sections/EmpDetailDialog';

import EditModeButton from './components/EditModeButton';
import SeatEditDialog from './components/SeatEditDialog';
import SeatMap from './components/SeatMap';
import { OFFICE_TABS } from './constants/officeImages';
import { EMPTY_SEAT_COLOR } from './constants/teamColors';
import { useEditPermission } from './hooks/useEditPermission';
import { useOfficeLayout } from './hooks/useOfficeLayout';
import { OfficeType, SeatInfo } from './types/seatTypes';
import { findSeatById } from './utils/seatChartParser';

const OrganizationLayout = () => {
  const pageTitle = '자리배치도';
  const { enqueueSnackbar } = useSnackbar();

  // 권한 체크
  const hasEditPermission = useEditPermission();

  // 탭 상태
  const [activeTab, setActiveTab] = useState<OfficeType>('main');

  // 수정 모드
  const [editMode, setEditMode] = useState(false);

  // 좌석 데이터 관리
  const { seats, loading, error, loadSeats, updateSeat, saveSeats, resetSeats } = useOfficeLayout();

  // 직원 데이터
  const [employeeList, setEmployeeList] = useState<IEmployee[]>([]);

  // 좌석 편집 다이얼로그
  const [selectedSeat, setSelectedSeat] = useState<SeatInfo | null>(null);
  const [seatDialogOpen, setSeatDialogOpen] = useState(false);

  // 직원 상세 모달 state
  const [employeeDetailModalInfo, setEmployeeDetailModalInfo] = useState<EmpDetailDialogProps>({
    open: false,
    onClose: () => setEmployeeDetailModalInfo((prev) => ({ ...prev, open: false })),
  });

  // 현재 탭의 사무실 SID
  const currentOfficeSid = OFFICE_TABS.find((t) => t.value === activeTab)?.officeSid || 1;

  /**
   * 조직도 데이터에서 전체 직원 목록 추출
   */
  const flattenEmployees = useCallback((org: IOrganizationChart): IEmployee[] => {
    const employees: IEmployee[] = [];
    if (org.empList) employees.push(...org.empList);
    if (org.childDept) {
      org.childDept.forEach((dept) => employees.push(...flattenEmployees(dept)));
    }
    return employees;
  }, []);

  /**
   * 직원 데이터 로드
   */
  useEffect(() => {
    const loadEmployees = async () => {
      const orgData = await commonApi.getOrganizationData();
      if (orgData) {
        const employees = flattenEmployees(orgData);
        const activeEmployees = employees.filter((emp) => !emp.tmntYn);
        setEmployeeList(activeEmployees);
      }
    };
    loadEmployees();
  }, [flattenEmployees]);

  /**
   * 좌석 데이터 로드 (탭 변경 시)
   */
  useEffect(() => {
    loadSeats(currentOfficeSid);
  }, [currentOfficeSid, loadSeats]);

  /**
   * 에러 표시
   */
  useEffect(() => {
    if (error) {
      enqueueSnackbar(error, { variant: 'error' });
    }
  }, [error, enqueueSnackbar]);

  /**
   * 탭 변경 핸들러
   */
  const handleTabChange = (tab: OfficeType) => {
    if (editMode) {
      // 수정 모드에서 탭 변경 시 확인
      if (!window.confirm('수정 중인 내용이 있습니다. 저장하지 않고 이동하시겠습니까?')) {
        return;
      }
      setEditMode(false);
      resetSeats();
    }
    setActiveTab(tab);
  };

  /**
   * 좌석 클릭 핸들러 (편집 모드)
   */
  const handleSeatClick = (seatId: string, x: number, y: number) => {
    const existingSeat = findSeatById(seats, seatId);

    const seatInfo: SeatInfo = existingSeat || {
      seatId,
      x,
      y,
      teamColor: EMPTY_SEAT_COLOR,
    };

    setSelectedSeat(seatInfo);
    setSeatDialogOpen(true);
  };

  /**
   * 좌석 더블클릭 핸들러 (직원 상세 정보 조회)
   */
  const handleSeatDoubleClick = async (seat: SeatInfo) => {
    if (!seat.employeeId || seat.employeeId === 0) {
      enqueueSnackbar('공석입니다.', { variant: 'info' });
      return;
    }

    try {
      const result = await employeeApi.findEmployee(seat.employeeId);

      if (!result) {
        enqueueSnackbar('직원 정보를 불러오는데 실패했습니다.', { variant: 'error' });
        return;
      }

      setEmployeeDetailModalInfo({
        open: true,
        selectedValue: {
          ...result.data,
          ...(result.data.user.profileImgUrl && { profileImgUrl: result.data.user.profileImgUrl }),
        },
        onClose: () => setEmployeeDetailModalInfo((prev) => ({ ...prev, open: false })),
      });
    } catch (error) {
      console.error(error);
      enqueueSnackbar('직원 정보 조회 중 오류가 발생했습니다.', { variant: 'error' });
    }
  };

  /**
   * 좌석 편집 확인 핸들러
   */
  const handleSeatConfirm = (updatedSeat: SeatInfo) => {
    updateSeat(updatedSeat);
    setSeatDialogOpen(false);
    setSelectedSeat(null);
  };

  /**
   * 저장 핸들러
   */
  const handleSave = async () => {
    const success = await saveSeats(currentOfficeSid, activeTab);
    if (success) {
      enqueueSnackbar('저장되었습니다.', { variant: 'success' });
      setEditMode(false);
    }
  };

  /**
   * 취소 핸들러
   */
  const handleCancel = () => {
    resetSeats();
    setEditMode(false);
  };

  return (
    <Page title={pageTitle}>
      {/* 탭 */}
      {/* <OfficeTabs activeTab={activeTab} onTabChange={handleTabChange} /> */}

      {/* 콘텐츠 영역 */}
      <ContentContainer>
        {/* 수정 버튼 */}
        {hasEditPermission && (
          <EditModeButton
            editMode={editMode}
            loading={loading}
            onEdit={() => setEditMode(true)}
            onSave={handleSave}
            onCancel={handleCancel}
          />
        )}

        {/* 도면 */}
        <SeatMap
          officeType={activeTab}
          seats={seats}
          editMode={editMode}
          onSeatClick={handleSeatClick}
          onSeatDoubleClick={handleSeatDoubleClick}
        />
      </ContentContainer>

      {/* 좌석 편집 다이얼로그 */}
      <SeatEditDialog
        open={seatDialogOpen}
        seat={selectedSeat}
        employeeList={employeeList}
        onClose={() => {
          setSeatDialogOpen(false);
          setSelectedSeat(null);
        }}
        onConfirm={handleSeatConfirm}
      />

      {/* 직원 상세 다이얼로그 */}
      <EmpDetailDialog {...employeeDetailModalInfo} />
    </Page>
  );
};

export default observer(OrganizationLayout);

const ContentContainer = styled(Box)(({ theme }) => ({
  position: 'relative',
  padding: theme.spacing(2),
  borderRadius: theme.shape.borderRadius,
  border: `1px solid ${theme.palette.divider}`,
  minHeight: 500,
}));
